import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { createAuthFor, syncAuthPassword } from '../../api';
import { generateTempPassword } from '../../core/helpers';


function EditEmployeeModal({ data, onClose }) {
  const { employees, setEmps, setAllUsers, showToast, can, clients, tasks } = useApp();
  const emp = employees.find(e => e.id === data?.empId);
  const [reassignTo, setReassignTo] = useState("");
  const [form, setForm] = useState({
    name:     emp?.name     || "",
    email:    emp?.email    || "",
    phone:    emp?.phone    || "",
    role:     emp?.role     || "employee",
    password: "",
    permissions: emp?.permissions || {},
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  if (!emp) return null;

  // #G5 Reassign this person's clients & tasks to another team member
  const myClientCount = clients.filter(c => c.assignedTo === emp.id).length;
  const myTaskCount   = tasks.filter(t => t.assignedTo === emp.id && t.status !== "completed" && t.status !== "cancelled").length;
  const doReassign = async (targetId) => {
    let moved = 0;
    for (const c of clients.filter(c => c.assignedTo === emp.id)) { await sb.update("companies","id",c.id,{ assigned_to: targetId }); moved++; }
    for (const t of tasks.filter(t => t.assignedTo === emp.id)) { await sb.update("tasks","id",t.id,{ assigned_to: targetId }); moved++; }
    return moved;
  };

  const save = async () => {
    if (!form.name || !form.email) { showToast("Name and email required","error"); return; }
    const updates = {
      name:  form.name,
      email: form.email.toLowerCase(),
      phone: form.phone || null,
      role:  form.role,
      permissions: form.permissions,
    };
    if (form.password && form.password.length > 0 && form.password.length < 6) { showToast("Password must be 6+ chars","error"); return; }

    await sb.updateUser(emp.id, {
      name:  form.name,
      email: form.email.toLowerCase(),
      phone: form.phone || null,
      role:  form.role,
      permissions: form.permissions,
    });
    if (form.password && form.password.length >= 6) {
      const res = await syncAuthPassword(emp.id, form.password); // #Phase2 sync to Supabase Auth login
      if (!res.ok) { showToast("Profile saved, but the password change failed: " + (res.error || "try Reset Password instead"), "error"); return; }
    }
    setEmps(es => es.map(e => e.id === emp.id ? { ...e, ...updates } : e));
    setAllUsers(us => us.map(u => u.id === emp.id ? { ...u, ...updates } : u));
    showToast("Employee updated!", "success");
    onClose();
  };

  return (
    <div className="modal-box" style={{maxWidth:480}}>
      <div className="modal-head"><div className="modal-title">Edit Employee — {emp.name}</div><button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button></div>
      <div className="modal-body">
        <div className="form-grid">
          <div className="f-group"><label className="f-label">Full Name <span className="f-req">*</span></label><input className="f-input" value={form.name} onChange={e=>set("name",e.target.value)}/></div>
          <div className="f-group"><label className="f-label">Email <span className="f-req">*</span></label><input className="f-input" type="email" value={form.email} onChange={e=>set("email",e.target.value)}/></div>
          <div className="f-group"><label className="f-label">Phone</label><input className="f-input" value={form.phone} onChange={e=>set("phone",e.target.value)}/></div>
          <div className="f-group"><label className="f-label">Role</label>
            <select className="f-select" value={form.role} onChange={e=>set("role",e.target.value)}>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="sales">Sales</option>
            </select>
          </div>
          <div className="f-group"><label className="f-label">New Password (leave blank to keep current)</label><input className="f-input" type="password" placeholder="Min 6 characters" value={form.password} onChange={e=>set("password",e.target.value)}/></div>
          <div className="f-group">
            <label className="f-label">Reassign this person's work</label>
            <div style={{ display:"flex", gap:8 }}>
              <select className="f-select" value={reassignTo} onChange={e=>setReassignTo(e.target.value)} style={{ flex:1 }}>
                <option value="">Select a team member…</option>
                {employees.filter(x=>x.id!==emp.id).map(x=><option key={x.id} value={x.id}>{x.name} ({x.role})</option>)}
              </select>
              <button className="btn" onClick={async()=>{
                if (!reassignTo) { showToast("Pick a team member first","error"); return; }
                const m = await doReassign(reassignTo);
                showToast(`Reassigned ${m} item(s).`,"success");
              }}>Reassign</button>
            </div>
            <div className="f-hint">{myClientCount} client(s) · {myTaskCount} open task(s) assigned. Reassign before removing this person.</div>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        {can("team_reports","delete") && <button className="btn btn-sm" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5",marginRight:"auto"}}
          onClick={async()=>{
            if ((myClientCount > 0 || myTaskCount > 0) && !reassignTo) {
              showToast("This person has assigned clients/tasks — choose someone to reassign them to first.","error"); return;
            }
            try {
              await sb.createDeleteRequest({
                entity_type: "employees",
                entity_id: emp.id,
                reason: `Delete employee ${emp.name}`,
                reassign_to: reassignTo || null,
              });
              showToast("Delete request submitted — awaiting admin approval", "success");
              onClose();
            } catch (err) {
              showToast(err?.message || "Failed to submit delete request", "error");
            }
          }}><InlineIcon name="trash" size={14} gap={5}>Request Delete</InlineIcon></button>}
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save Changes</button>
      </div>
    </div>
  );
}


export { EditEmployeeModal };
